import { useState, useMemo, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { useHelpdeskNavigation } from '../hooks/useHelpdeskNavigation';
import { usePersistedFilters } from '../hooks/usePersistedFilters';
import { useScrollEdges } from '../hooks/useScrollEdges';
import { useDragScroll } from '../hooks/useDragScroll';
import { TH_CLASS, TD_CLASS } from '../lib/tableStyles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Archive,
  UserX,
  Users,
  Plus,
  Calendar,
  Mail,
  Ticket,
  Building2,
  X
} from 'lucide-react';
import {
  useCustomers,
  useCustomerStats
} from '@/hooks/useCustomers';
import { useDateFormat } from '@/hooks/useDateFormat';
import { CustomerListSkeleton } from '@/components/LoadingSkeleton';
import { MultiSelectFilter } from '@/components/MultiSelectFilter';
import type {
  CustomerFilterRequest
} from '@/types/customers';
import { Route } from '../routes/helpdesk/customers/index';


export const Customers: React.FC = () => {
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { formatDateTime } = useDateFormat();
  const { availableOrganizations } = useAuth();
  const searchParams = Route.useSearch();

  // Define default filter values for localStorage persistence
  const defaultFilterValues = {
    sortBy: 'fullname',
    sortDescending: false,
    archived: false,
    page: 1,
    pageSize: 20,
  };

  // Initialize filter persistence (global across all orgs)
  const storageKey = `helpdesk.customers.filters`;
  const { updateFilters, clearFilters: clearPersistedFilters, isInitialized } = usePersistedFilters(
    storageKey,
    defaultFilterValues,
    searchParams
  );

  // Filter state - use URL params
  const [searchInput, setSearchInput] = useState(searchParams.search || '');

  // Sync search input with URL params when they change
  useEffect(() => {
    setSearchInput(searchParams.search || '');
  }, [searchParams.search]);

  // Get current filter values from URL
  const organizationFiltersRaw = searchParams.organizationIds ?? [];

  // Auto-select all organizations if none selected (only after filter restoration is complete)
  useEffect(() => {
    if (isInitialized && availableOrganizations && availableOrganizations.length > 0 && organizationFiltersRaw.length === 0) {
      updateFilters({ organizationIds: availableOrganizations.map(org => org.id) });
    }
  }, [isInitialized, availableOrganizations, organizationFiltersRaw.length, updateFilters]);

  // Get current filter values from URL
  const searchQuery = searchParams.search || '';
  const organizationFilters = searchParams.organizationIds ?? [];
  const sortBy = searchParams.sortBy || 'fullname';
  const sortDescending = searchParams.sortDescending ?? false;
  const showArchived = searchParams.archived ?? false;
  const currentPage = searchParams.page || 1;
  const pageSize = searchParams.pageSize || 20;

  // Build filter object for query
  const filter = useMemo(
    (): CustomerFilterRequest => ({
      page: currentPage,
      pageSize,
      sortBy,
      sortDescending,
      ...(showArchived && { archived: true }),
      ...(searchQuery && { searchQuery }),
      ...(organizationFilters.length > 0 && { organizationIds: organizationFilters }),
    }),
    [
      currentPage,
      pageSize,
      sortBy,
      sortDescending,
      showArchived,
      searchQuery,
      organizationFilters,
    ]
  );
  
  // Use TanStack Query hook
  const { data, isLoading, isFetching, error, isError } = useCustomers(filter);
  const { data: stats, isFetching: isStatsFetching } = useCustomerStats(organizationFilters.length > 0 ? organizationFilters : undefined);

  // Ensure we always have arrays
  const customers = data?.customers || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  const {
    ref: tableScrollRef,
    edges: scrollEdges,
    onScroll: updateScrollEdges,
  } = useScrollEdges<HTMLDivElement>([customers.length, isLoading]);

  const tableDragProps = useDragScroll(tableScrollRef);

  const handleSearch = () => {
    updateFilters({ search: searchInput || undefined, page: 1 });
  };

  const handleSort = (field: string) => {
    const newSortDescending = sortBy === field ? !sortDescending : true;
    updateFilters({ sortBy: field, sortDescending: newSortDescending });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    updateFilters({ pageSize: newPageSize, page: 1 });
  };

  // Show skeleton while filters are being restored
  if (!isInitialized) {
    return <CustomerListSkeleton />;
  }

  // Show full skeleton only when no data exists and loading
  if (isLoading && !data) {
    return <CustomerListSkeleton />;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Customer Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Manage your customer relationships and support history
          </p>
        </div>
        <Link to={getPath.newCustomer()} className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
        </Link>
      </div>

      {/* Error Display */}
      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error?.message || 'An error occurred while loading customers'}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isStatsFetching ? (
                <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
              ) : (
                stats?.totalCustomers ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Excluding archived customers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New This Month</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isStatsFetching ? (
                <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
              ) : (
                stats?.newCustomersThisMonth ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              New customers this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">With Open Tickets</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isStatsFetching ? (
                <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
              ) : (
                stats?.activeCustomers ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Customers with open tickets
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Tickets/Customer</CardTitle>
            <Badge variant="outline" className="h-4 px-1">
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isStatsFetching ? (
                <div className="h-8 w-12 bg-muted animate-pulse rounded"></div>
              ) : (
                stats?.averageTicketsPerCustomer ? stats.averageTicketsPerCustomer.toFixed(1) : '0'
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Average support tickets per customer
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="space-y-4">
          {/* First row: Search and main filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Search
              </label>
              <div className="flex">
                <div className="relative flex-1">
                  <Input
                    type="text"
                    placeholder="Search customers..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-r-none pr-8"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput('');
                        updateFilters({ search: undefined, page: 1 });
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Button
                  onClick={handleSearch}
                  className="rounded-l-none"
                  disabled={isLoading}
                >
                  Search
                </Button>
              </div>
            </div>

            {/* Archived customers are hidden from the list, stats and pickers unless asked for */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Status
              </label>
              <Select
                value={showArchived ? 'archived' : 'active'}
                onValueChange={(value) => updateFilters({ archived: value === 'archived', page: 1 })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active customers</SelectItem>
                  <SelectItem value="archived">Archived customers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Organization filter - only show when user has multiple orgs */}
            {availableOrganizations && availableOrganizations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Organization
                </label>
                <MultiSelectFilter
                  label="Organization"
                  options={availableOrganizations.map(org => ({ value: org.id, label: org.name }))}
                  selectedValues={organizationFilters}
                  onChange={(values) => {
                    updateFilters({ organizationIds: values, page: 1 });
                  }}
                  requireAtLeastOne={true}
                  showSelectAllControls={true}
                />
              </div>
            )}

            {/* Clear Filters button - only show when user has multiple orgs */}
            {availableOrganizations && availableOrganizations.length > 1 && (
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchInput('');
                    clearPersistedFilters();
                  }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Customers Table */}
      <Card>
        <div className="relative">
          {/* Bounded height keeps the horizontal scrollbar in view instead of parking it below
              the last row, and gives the sticky header a scrollport to resolve against. */}
          <div
            ref={tableScrollRef}
            onScroll={updateScrollEdges}
            {...tableDragProps}
            className="max-h-[calc(100vh-22rem)] overflow-auto"
          >
            {/* Fixed layout: Customer absorbs the slack, every other column is pinned, so a
                long email or organisation name truncates instead of widening its column. */}
            <table className="w-full table-fixed min-w-[460px] md:min-w-[720px] lg:min-w-[920px]">
              <thead className="sticky top-0 z-20 bg-muted">
                <tr>
                  <th
                    className={TH_CLASS}
                    onClick={() => handleSort('fullname')}
                  >
                    Customer
                    {sortBy === 'fullname' && (
                      <span className="ml-1">{sortDescending ? '↓' : '↑'}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[260px] hidden md:table-cell`}
                    onClick={() => handleSort('email')}
                  >
                    Contact
                    {sortBy === 'email' && (
                      <span className="ml-1">{sortDescending ? '↓' : '↑'}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[200px] hidden lg:table-cell`}
                    onClick={() => handleSort('organization')}
                  >
                    Organization
                    {sortBy === 'organization' && (
                      <span className="ml-1">{sortDescending ? '↓' : '↑'}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[90px]`}
                    onClick={() => handleSort('totaltickets')}
                  >
                    Tickets
                    {sortBy === 'totaltickets' && (
                      <span className="ml-1">{sortDescending ? '↓' : '↑'}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[150px]`}
                    onClick={() => handleSort('createdat')}
                  >
                    Joined
                    {sortBy === 'createdat' && (
                      <span className="ml-1">{sortDescending ? '↓' : '↑'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border">
                {isFetching && customers.length === 0
                  ? // Show table skeleton when filtering/loading with no current data
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={`loading-${index}`} className="animate-pulse">
                        <td className={TD_CLASS}>
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-muted"></div>
                            <div>
                              <div className="h-4 bg-muted rounded w-32 mb-1"></div>
                              <div className="h-3 bg-muted rounded w-24"></div>
                            </div>
                          </div>
                        </td>
                        <td className={`${TD_CLASS} hidden md:table-cell`}>
                          <div className="h-4 bg-muted rounded w-32"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="h-4 bg-muted rounded w-24"></div>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="h-5 bg-muted rounded-full w-16"></div>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="h-4 bg-muted rounded w-20"></div>
                        </td>
                      </tr>
                    ))
                  : customers.map((customer) => (
                      <tr
                        key={customer.id}
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => {
                          navigateTo.customer(customer.id);
                        }}
                      >
                        <td className={TD_CLASS}>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium flex-shrink-0">
                              {customer.firstName[0]}{customer.lastName[0]}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="font-medium text-foreground text-sm sm:text-base truncate" title={customer.fullName}>{customer.fullName}</div>
                                {customer.erasedAt ? (
                                  <Badge variant="secondary" className="gap-1 flex-shrink-0">
                                    <UserX className="h-3 w-3" />
                                    Erased
                                  </Badge>
                                ) : customer.archivedAt && (
                                  <Badge variant="secondary" className="gap-1 flex-shrink-0">
                                    <Archive className="h-3 w-3" />
                                    Archived
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground hidden sm:block">ID: {customer.id.slice(0, 8)}</div>
                            </div>
                          </div>
                        </td>
                        <td className={`${TD_CLASS} hidden md:table-cell`}>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-foreground truncate" title={customer.email}>{customer.email}</span>
                          </div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-foreground truncate" title={customer.organizationName || 'Unknown'}>{customer.organizationName || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className={TD_CLASS}>
                          <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {customer.totalTickets}
                          </span>
                        </td>
                        <td className={`${TD_CLASS} text-xs text-muted-foreground`}>
                          {formatDateTime(customer.createdAt)}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          {/* Sits outside the scrollport so it stays pinned to the card edge rather than
              travelling with the content. `foreground` inverts with the theme. */}
          {scrollEdges.left && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-foreground/20 to-transparent"
            />
          )}
          {scrollEdges.right && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-foreground/20 to-transparent"
            />
          )}
        </div>

        {/* Pagination and Record Count */}
        <div className="bg-background px-6 py-3 border-t border-border">
          {totalPages > 1 ? (
            <div className="flex items-center justify-between">
              {/* Mobile stacked layout */}
              <div className="flex flex-col gap-3 sm:hidden w-full">
                {/* Page size and record count - centered */}
                <div className="flex flex-col gap-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <label className="text-xs text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-xs text-foreground">per page</span>
                  </div>
                  <p className="text-xs text-foreground">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
                    <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>
                {/* Pagination buttons - full width */}
                <div className="flex justify-between gap-2">
                  <Button
                    variant="outline"
                    onClick={() => updateFilters({ page: Math.max(1, currentPage - 1) })}
                    disabled={currentPage === 1 || isLoading}
                    className="flex-1"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateFilters({ page: Math.min(totalPages, currentPage + 1) })}
                    disabled={currentPage === totalPages || isLoading}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              </div>

              {/* Desktop layout */}
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-foreground">per page</span>
                  </div>
                  <div>
                    <p className="text-sm text-foreground">
                      Showing{' '}
                      <span className="font-medium">
                        {(currentPage - 1) * pageSize + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, totalCount)}
                      </span>{' '}
                      of <span className="font-medium">{totalCount}</span> results
                    </p>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <Button
                      variant="outline"
                      onClick={() => updateFilters({ page: Math.max(1, currentPage - 1) })}
                      disabled={currentPage === 1 || isLoading}
                      className="rounded-l-md"
                    >
                      Previous
                    </Button>
                    {/* Page Numbers */}
                    {(() => {
                      const pages: number[] = [];
                      const showPages = 5; // Number of page buttons to show

                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      const endPage = Math.min(totalPages, startPage + showPages - 1);

                      // Adjust start if we're near the end
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }

                      return pages.map(page => (
                        <Button
                          key={page}
                          variant={page === currentPage ? "default" : "outline"}
                          onClick={() => updateFilters({ page })}
                          disabled={isLoading}
                          className="min-w-[40px]"
                        >
                          {page}
                        </Button>
                      ));
                    })()}
                    <Button
                      variant="outline"
                      onClick={() => updateFilters({ page: Math.min(totalPages, currentPage + 1) })}
                      disabled={currentPage === totalPages || isLoading}
                      className="rounded-r-md"
                    >
                      Next
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          ) : (
            // Show record count and page size selector even when there's only one page
            <div className="flex items-center justify-between">
              {/* Mobile stacked layout */}
              <div className="flex flex-col gap-2 sm:hidden w-full text-center">
                <div className="flex items-center justify-center gap-2">
                  <label className="text-xs text-foreground">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs text-foreground">per page</span>
                </div>
                <p className="text-xs text-foreground">
                  Showing{' '}
                  <span className="font-medium">
                    {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{' '}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>

              {/* Desktop layout */}
              <div className="hidden sm:flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-foreground">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-foreground">per page</span>
                </div>
                <div>
                  <p className="text-sm text-foreground">
                    Showing{' '}
                    <span className="font-medium">
                      {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalCount)}
                    </span>{' '}
                    of <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {customers.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {showArchived ? 'No archived customers found' : 'No customers found'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {showArchived
                ? 'Customers you archive appear here.'
                : searchQuery || organizationFilters.length > 0 ? 'Try adjusting your search or filter terms.' : 'Get started by adding your first customer.'}
            </p>
            <Link to={getPath.newCustomer()}>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Customer
              </Button>
            </Link>
          </div>
        )}
      </Card>

    </div>
  );
};